/** Bounded RIFF header inspection, not a WebP decoder or full format validator.
 * Layouts: https://developers.google.com/speed/webp/docs/riff_container
 */
export function webpDimensions(bytes: Uint8Array, fileSize: number): { width: number; height: number } | null {
  const view = new DataView(bytes.buffer, bytes.byteOffset, bytes.byteLength);
  const fourCC = (at: number) => String.fromCharCode(...bytes.subarray(at, at + 4));
  if (bytes.length < 12 || fourCC(0) !== 'RIFF' || fourCC(8) !== 'WEBP') return null;
  const end = view.getUint32(4, true) + 8;
  if (end < 20 || end > fileSize || end % 2 !== 0) throw new Error('Invalid WebP container size.');
  const uint24 = (at: number) => bytes[at]! + bytes[at + 1]! * 256 + bytes[at + 2]! * 65536;
  for (let offset = 12; offset < end;) {
    if (offset + 8 > bytes.length) throw new Error('WebP dimensions exceed the 1 MiB header inspection limit.');
    const kind = fourCC(offset);
    const size = view.getUint32(offset + 4, true);
    const data = offset + 8;
    const next = data + size + (size % 2);
    if (next > end) throw new Error('Truncated WebP chunk.');
    const requireBytes = (minimum: number) => {
      if (size < minimum || data + minimum > bytes.length) throw new Error('Truncated WebP dimension header.');
    };
    if (kind === 'VP8X') {
      requireBytes(10);
      if (size !== 10) throw new Error('Invalid extended WebP header size.');
      return { width: uint24(data + 4) + 1, height: uint24(data + 7) + 1 };
    }
    if (kind === 'VP8L') {
      requireBytes(5);
      if (bytes[data] !== 0x2f) throw new Error('Invalid lossless WebP signature.');
      const bits = view.getUint32(data + 1, true);
      return { width: (bits & 0x3fff) + 1, height: ((bits >>> 14) & 0x3fff) + 1 };
    }
    if (kind === 'VP8 ') {
      requireBytes(10);
      if ((bytes[data]! & 1) !== 0 || bytes[data + 3] !== 0x9d || bytes[data + 4] !== 0x01 || bytes[data + 5] !== 0x2a) throw new Error('Invalid WebP key-frame header.');
      return { width: view.getUint16(data + 6, true) & 0x3fff, height: view.getUint16(data + 8, true) & 0x3fff };
    }
    offset = next;
  }
  throw new Error('WebP has no supported dimension header.');
}
